import React, { useState, useEffect } from "react";

import {
  numberWithCommas,
  renderDecimalMinuteSeconds,
  toTitleCase,
} from "../../Shared/Functions";

// ESRI Components
import * as geometryEngineAsync from "@arcgis/core/geometry/geometryEngineAsync";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";

import Tooltip from "./Tooltip";
import MeasurementHandler from "./MeasurementHandler";
import { Segment, Dropdown } from "semantic-ui-react";

const MeasurementWidget = ({ view, parcels_service_url, activeTool }) => {
  const [active, setActive] = useState(false);
  const [measurementHandler, setMeasurementHandler] = useState(null);
  const [tooltipMessage, setTooltipMessage] = useState("test");

  const [geometry, setGeometry] = useState(null);
  const [lengthUnit, setLengthUnit] = useState(null);
  const [areaUnit, setAreaUnit] = useState(null);
  const [locUnit, setLocUnit] = useState(null);
  const [length, setLength] = useState(null);
  const [area, setArea] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationLabelX, setLocationLabelX] = useState(null);
  const [locationLabelY, setLocationLabelY] = useState(null);
  const [bearing, setBearing] = useState(null);

  const [distance, setDistance] = useState(null);
  const [unit, setUnit] = useState(null);

  // console.warn("MeasurementWidget", {
  //   geometry,
  //   areaUnit,
  //   lengthUnit,
  //   // view,
  //   // parcels_service_url,
  //   // activeTool,
  //   // measurementHandler,
  //   // tooltipMessage,
  //   // active,
  //   // ready: activeTool && activeTool.tool != "Measurement",
  // });

  useEffect(() => {
    // Intiailze the measurement handler
    setMeasurementHandler(
      new MeasurementHandler({
        setActive,
        view,
        parcels_service_url,
        setTooltipMessage,
        setGeometry,
        setLengthUnit,
        setAreaUnit,
        setLocUnit,
        setLength,
        setArea,
        setLocation,
        setLocationLabelX,
        setLocationLabelY,
        setBearing,
        setDistance,
        setUnit,
      })
    );
  }, []);

  useEffect(() => {
    if (activeTool && activeTool.tool === "Measurement") {
      // start up the measurementHandler
      measurementHandler.start(activeTool.type);
    } else if (measurementHandler) {
      measurementHandler.cancel();
    }
  }, [activeTool]);

  useEffect(() => {
    if (location) {
      buildLocation();
    }
  }, [location]);

  useEffect(() => {
    if (locUnit) {
      calculateLocation();
    }
  }, [locUnit]);

  useEffect(() => {
    if (lengthUnit) {
      calculateLength();
    }
  }, [lengthUnit]);

  useEffect(() => {
    if (areaUnit) {
      calculateArea();
    }
  }, [areaUnit]);

  if (active) {
    return (
      <>
        <Segment size="tiny" color="grey">
          <table cellPadding="0" cellSpacing="0" border="0">
            <tbody>
              <tr>
                <td>
                  <p
                    style={{
                      marginRight: "0.25em",
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    Distance:{" "}
                  </p>
                </td>
                <td>
                  <p style={{ marginRight: "1em" }}>
                    {numberWithCommas(distance)} {unit}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p
                    style={{
                      marginRight: "0.25em",
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    Bearing:{" "}
                  </p>
                </td>
                <td>
                  <p style={{ marginRight: "1em" }}>{bearing}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </Segment>

        {tooltipMessage && (
          <Tooltip view={view} message={tooltipMessage} visible={true} />
        )}
      </>
    );
  }

  if (activeTool && activeTool.tool != "Measurement") {
    return null;
  }

  if (!geometry) {
    return null;
  }

  return (
    <>
      <Segment color="grey" size="tiny">
        <table cellPadding="0" cellSpacing="0" border="0">
          <tbody>
            {geometry.type === "polyline" || geometry.type === "polygon"
              ? renderLength()
              : null}
            {geometry.type === "polygon" ? renderArea() : null}
            {geometry.type === "point" ? renderLocation() : null}
          </tbody>
        </table>
      </Segment>
    </>
  );

  function renderLength() {
    return (
      <tr>
        <td>
          <p
            style={{
              marginRight: "0.25em",
              textAlign: "right",
              fontWeight: "bold",
            }}
          >
            Length:{" "}
          </p>
        </td>
        <td>
          <p style={{ marginRight: "1em" }}>{numberWithCommas(length)}</p>
        </td>
        <td style={{ minWidth: "100px" }}>
          <Dropdown
            compact
            fluid
            selection
            placeholder={toTitleCase(lengthUnit)}
            options={renderLengthOptions()}
            onClick={(value) => {
              // console.error("onClick", value);
            }}
          />
        </td>
      </tr>
    );
  }

  function renderArea() {
    return (
      <tr>
        <td>
          <p
            style={{
              marginRight: "0.25em",
              textAlign: "right",
              fontWeight: "bold",
            }}
          >
            Area:{" "}
          </p>
        </td>
        <td>
          <p style={{ marginRight: "1em" }}>{numberWithCommas(area)}</p>
        </td>
        <td style={{ minWidth: "100px" }}>
          <Dropdown
            compact
            fluid
            selection
            placeholder={toTitleCase(areaUnit)}
            options={renderAreaOptions()}
          />
        </td>
      </tr>
    );
  }

  function renderLocation() {
    return (
      <tr>
        <td>
          <table
            cellPadding="0"
            cellSpacing="0"
            border="0"
            style={{ marginRight: "1em" }}
          >
            <tbody>
              <tr>
                <td style={{ textAlign: "right" }}>
                  <p>Latitude:</p>
                </td>
                <td>
                  <p style={{ marginLeft: "0.5em", textAlign: "right" }}>
                    {locationLabelY}
                  </p>
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: "right" }}>
                  <p>Longitude:</p>
                </td>
                <td>
                  <p style={{ marginLeft: "0.5em", textAlign: "right" }}>
                    {locationLabelX}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
        <td style={{ minWidth: "80px" }}>
          <Dropdown selection compact fluid placeholder={locUnit}>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setLocUnit("Lat/Lon")}>
                Lat/Lon
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setLocUnit("DMS")}>
                DMS
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </td>
      </tr>
    );
  }

  function calculateLocation() {
    const outSpatialReference = new SpatialReference({
      wkid: 4326,
    });
    projection.load().then(() => {
      var mp = projection.project([geometry], outSpatialReference);
      setLocation(mp[0]);
    });
  }

  function calculateLength() {
    geometryEngineAsync.planarLength(geometry, lengthUnit).then((result) => {
      setLength(result.toFixed(6));
    });
  }

  function calculateArea() {
    // Simplifiy the geoemtry in case the topology is invalid
    geometryEngineAsync.simplify(geometry).then((result) => {
      // calculate the area using planar
      geometryEngineAsync.planarArea(result, areaUnit).then((result) => {
        setArea(result.toFixed(6));
      });
    });
  }

  function buildLocation() {
    // convert it to lat long or decimal degrees

    setLocationLabelX(
      locUnit === "DMS"
        ? renderDecimalMinuteSeconds(location.x)
        : location.x.toFixed(6)
    );
    setLocationLabelY(
      locUnit === "DMS"
        ? renderDecimalMinuteSeconds(location.y)
        : location.y.toFixed(6)
    );
  }

  function renderLengthOptions() {
    return [
      {
        key: "Feet",
        text: "Feet",
        value: "Feet",
        onClick: () => {
          setLengthUnit("feet");
        },
      },
      {
        key: "Yards",
        text: "Yards",
        value: "Yards",
        onClick: () => {
          setLengthUnit("yards");
        },
      },
      {
        key: "Miles",
        text: "Miles",
        value: "Miles",
        onClick: () => {
          setLengthUnit("miles");
        },
      },
      {
        key: "Meters",
        text: "Meters",
        value: "Meters",
        onClick: () => {
          setLengthUnit("meters");
        },
      },
      {
        key: "Kilometers",
        text: "Kilometers",
        value: "Kilometers",
        onClick: () => {
          setLengthUnit("kilometers");
        },
      },
    ];
  }

  function renderAreaOptions() {
    return [
      {
        key: "Square Feet",
        text: "Square Feet",
        value: "Square Feet",
        onClick: () => {
          setAreaUnit("square-feet");
        },
      },
      {
        key: "Square Yards",
        text: "Square Yards",
        value: "Square Yards",
        onClick: () => {
          setAreaUnit("square-yards");
        },
      },
      {
        key: "Square Miles",
        text: "Square Miles",
        value: "Square Miles",
        onClick: () => {
          setAreaUnit("square-miles");
        },
      },
      {
        key: "Acres",
        text: "Acres",
        value: "Acres",
        onClick: () => {
          setAreaUnit("acres");
        },
      },
    ];
  }
};

export default MeasurementWidget;
